import { GameId } from '@common'
import { useTranslations } from 'next-intl'
import Head from 'next/head'

import { FillerModule } from '@/components/FillerModule'
import { PageMetaTags } from '@/components/MetaTags'
import { Page } from '@/components/Page'
import { GameSelector } from '@/features/landing/GameSelector'
import Hero from '@/features/landing/Hero'
import { OurGames } from '@/features/storefronts/landing/OurGames'
import { StoreCards } from '@/features/storefronts/landing/StoreCard'
import { games as allGames } from '@/fixtures/games'

import { config } from '../../config'
import { LinkButton } from '../components/buttons/Button'
import { adminService } from '../services/AdminService'
import { wrapGetServerSideProps, setPublicPageCacheControl } from '../ssr/wrapper'
import { getAccountId } from '../utils/getAccountId'

import NeedSomeHelp from 'assets/images/characters/need-some-help.png'

import styles from './landingpage.module.scss'

type Props = {
  useNewLanding: boolean
  games: GameId[]
}
export default function Home({ useNewLanding, games }: Props) {
  const t = useTranslations()
  return (
    <Page>
      <PageMetaTags title={t('sitename')} description={t('home_description')} />
      <Head>
        <title>{t('sitename')}</title>
      </Head>
      {useNewLanding ? (
        <>
          <div className={styles.Sections}>
            <StoreCards games={games} />
            <OurGames games={games} />
          </div>
        </>
      ) : (
        <>
          <Hero />
          <GameSelector games={games} />
          <FillerModule
            className={styles.BottomFAQ}
            imageClassName={styles.BottomFAQImage}
            title={t('lan_need_some_help')}
            image={NeedSomeHelp}
            action={{ content: t('go_to_faq').toUpperCase(), link: '/help' }}
          >
            <span className={styles.Mobile}>
              {t.rich('lan_faq_body')}
              <LinkButton href="/help" type="Soft">
                {t('go_to_faq').toUpperCase()}
              </LinkButton>
            </span>
            <span className={styles.Desktop}>{t('lan_faq_body')}</span>
          </FillerModule>
        </>
      )}
    </Page>
  )
}

export const getServerSideProps = wrapGetServerSideProps(async (store, context, isRecognized, enabledStoreFronts) => {
  setPublicPageCacheControl(context, 30, 60)
  const useNewLanding = enabledStoreFronts.length > 0
  const gameIds = allGames.map((game) => game.slug)

  const viewerCountry = store.getState().config.viewerCountry
  const accountId = getAccountId(context)
  const isTestAccount = Boolean(accountId && (await adminService.getIsTestAccount(accountId)))
  const isSquadEnabled = (viewerCountry && config.SQUAD_ENABLED_COUNTRIES.includes(viewerCountry)) || isTestAccount

  const games = isSquadEnabled ? gameIds : gameIds.filter((game) => game !== 'squadbusters')

  return {
    props: {
      useNewLanding,
      games,
    },
  }
}, 'storeInitSSR')
